import { useState, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useHiveFundInvestFlow } from './useHiveFundInvestFlow';
import { WelcomeStep } from './steps/WelcomeStep';
import { EligibilityStep } from './steps/EligibilityStep';
import { AmountStep } from './steps/AmountStep';
import { PaymentMethodStep } from './steps/PaymentMethodStep';
import { ReviewStep } from './steps/ReviewStep';
import { SignatureStep } from './steps/SignatureStep';
import { DocEsignStep } from './steps/DocEsignStep';
import { CompleteStep } from './steps/CompleteStep';
import { PaymentStep } from '../Invest/steps/PaymentStep';
import { ProcessingStep } from '../Invest/steps/ProcessingStep';
import type { HiveFundInvestFlowProps, HiveFundFlowStep } from './HiveFundInvestFlow.types';
import { NAVIGABLE_STEPS } from './HiveFundInvestFlow.utils';
import { BRAND_ASSETS } from '../../../config';
import { SubscriptionWizard } from '../SubscriptionWizard';
import type { SubscriptionData } from '../../../services/api';

/**
 * Multi-step Hive Fund investment wizard.
 *
 * Responsibilities:
 *   - Renders the active step component
 *   - Owns fade transitions between steps
 *   - Manages progress bar and header chrome
 *   - Wires navigation (back, continue, submit)
 *
 * All state and business logic live in useHiveFundInvestFlow.
 */
export function HiveFundInvestFlow({ onBack, onComplete, onGoToSettings }: HiveFundInvestFlowProps) {
  const flow = useHiveFundInvestFlow();
  const [isVisible, setIsVisible] = useState(true);

  /** Cross-fade to a new step with a 150ms opacity transition. */
  const navigate = useCallback(
    (toStep: HiveFundFlowStep) => {
      setIsVisible(false);
      setTimeout(() => {
        flow.setStep(toStep);
        setIsVisible(true);
      }, 150);
    },
    [flow],
  );

  const handleBack = useCallback(() => {
    // If on signature, go back to docEsign (if required) or subscription
    if (flow.step === 'signature') {
      if (flow.requiresEsign) {
        navigate('docEsign');
      } else {
        navigate('subscription');
      }
      return;
    }

    // If on docEsign, go back to subscription
    if (flow.step === 'docEsign') {
      navigate('subscription');
      return;
    }

    // If on subscription, go back to review
    if (flow.step === 'subscription') {
      navigate('review');
      return;
    }

    // If on bankSelect, go back to paymentMethod
    if (flow.step === 'bankSelect') {
      navigate('paymentMethod');
      return;
    }

    // If on review and non-bank method was selected, skip bankSelect when going back
    if (flow.step === 'review' && flow.paymentMethod !== 'bank') {
      navigate('paymentMethod');
      return;
    }

    const idx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);
    if (idx > 0) navigate(NAVIGABLE_STEPS[idx - 1]);
    else onBack();
  }, [flow.step, flow.paymentMethod, navigate, onBack]);

  const handleContinue = useCallback(() => {
    const idx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);

    // PaymentMethod → bankSelect (if bank) or review (if crypto)
    if (flow.step === 'paymentMethod') {
      if (flow.paymentMethod === 'bank') {
        navigate('bankSelect');
      } else {
        navigate('review');
      }
      return;
    }

    // bankSelect → review
    if (flow.step === 'bankSelect') {
      navigate('review');
      return;
    }

    // review → subscription
    if (flow.step === 'review') {
      navigate('subscription');
      return;
    }

    // subscription → handled by onComplete callback (from SubscriptionWizard)

    // docEsign → signature
    if (flow.step === 'docEsign') {
      navigate('signature');
      return;
    }

    // signature → submit
    if (flow.step === 'signature') {
      setIsVisible(false);
      setTimeout(() => {
        setIsVisible(true);
        flow.submitInvestment();
      }, 150);
      return;
    }

    // Default: advance to next navigable step
    if (idx >= 0 && idx < NAVIGABLE_STEPS.length - 1) {
      navigate(NAVIGABLE_STEPS[idx + 1]);
    }
  }, [flow, navigate]);

  // Progress bar reflects user-decision steps only (not processing/complete)
  const progressIdx = NAVIGABLE_STEPS.indexOf(flow.step as (typeof NAVIGABLE_STEPS)[number]);
  const progressPercent =
    progressIdx >= 0 ? ((progressIdx + 1) / NAVIGABLE_STEPS.length) * 100 : 100;

  const canGoBack = flow.step !== 'processing' && flow.step !== 'complete';

  return (
    <div className="fixed inset-0 z-50 bg-background overflow-y-auto flex flex-col portal-typography">
      {/* Progress bar */}
      <div
        className="sticky top-0 left-0 right-0 h-1 bg-secondary z-[60]"
        role="progressbar"
        aria-valuenow={Math.round(progressPercent)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Investment flow progress"
      >
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Header */}
      <header className="flex items-center justify-between px-4 md:px-6 py-4 md:py-6">
        <button
          type="button"
          onClick={handleBack}
          disabled={!canGoBack}
          aria-label="Go back"
          className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors disabled:invisible"
        >
          <ArrowLeft className="w-5 h-5" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2">
          <img
            src={BRAND_ASSETS.logo.primary}
            alt="Honeyshares"
            className="h-11 object-contain"
          />
        </div>

        {/* Spacer keeps logo centered */}
        <div className="w-8" aria-hidden="true" />
      </header>

      {/* Step content */}
      <main className="flex-1 flex items-center justify-center px-4 md:px-6 pb-16">
        <div
          className="w-full max-w-2xl transition-opacity duration-150"
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          {flow.step === 'welcome' && (
            <WelcomeStep onContinue={handleContinue} minInvestment={flow.minInvestment} />
          )}

          {flow.step === 'eligibility' && (
            <EligibilityStep
              eligibility={flow.eligibility}
              onCheck={flow.checkEligibility}
              onContinue={handleContinue}
              onGoToSettings={onGoToSettings ?? onBack}
            />
          )}

          {flow.step === 'amount' && (
            <AmountStep
              investmentAmount={flow.investmentAmount}
              onAmountChange={flow.setInvestmentAmount}
              onContinue={handleContinue}
              canContinue={flow.canContinueFrom('amount')}
              minInvestment={flow.minInvestment}
              presetAmounts={flow.presetAmounts}
            />
          )}

          {flow.step === 'paymentMethod' && (
            <PaymentMethodStep
              paymentMethod={flow.paymentMethod}
              investmentAmount={flow.investmentAmount}
              onSelect={flow.setPaymentMethod}
              onContinue={handleContinue}
              canContinue={flow.canContinueFrom('paymentMethod')}
            />
          )}

          {flow.step === 'bankSelect' && (
            <PaymentStep
              investmentAmount={flow.investmentAmount}
              selectedAccountId={flow.selectedAccountId}
              onAccountSelect={flow.setSelectedAccountId}
              onSubmit={handleContinue}
              canSubmit={flow.canContinueFrom('bankSelect')}
            />
          )}

          {flow.step === 'review' && (
            <ReviewStep
              investmentAmount={flow.investmentAmount}
              paymentMethod={flow.paymentMethod || 'bank'}
              agreementAccepted={flow.agreementAccepted}
              onAgreementChange={flow.setAgreementAccepted}
              acceptTerms={flow.acceptTerms}
              onAcceptTermsChange={flow.setAcceptTerms}
              acceptRiskDisclosure={flow.acceptRiskDisclosure}
              onAcceptRiskDisclosureChange={flow.setAcceptRiskDisclosure}
              acceptAchAuth={flow.acceptAchAuth}
              onAcceptAchAuthChange={flow.setAcceptAchAuth}
              acceptCryptoIrrevocable={flow.acceptCryptoIrrevocable}
              onAcceptCryptoIrrevocableChange={flow.setAcceptCryptoIrrevocable}
              onConfirm={handleContinue}
              canConfirm={flow.canContinueFrom('review')}
            />
          )}

          {flow.step === 'subscription' && (
            <SubscriptionWizard
              onComplete={(data: SubscriptionData) => {
                flow.setSubscriptionData(data);
                if (flow.requiresEsign) {
                  navigate('docEsign');
                } else {
                  navigate('signature');
                }
              }}
              onBack={() => navigate('review')}
              investmentAmount={flow.investmentAmount}
              dealTitle="The Hive Fund"
            />
          )}

          {flow.step === 'docEsign' && (
            <DocEsignStep
              esignDocs={flow.esignDocs}
              signedDocIds={flow.signedDocIds}
              onDocSigned={flow.markDocSigned}
              onContinue={handleContinue}
              canContinue={flow.canContinueFrom('docEsign')}
            />
          )}

          {flow.step === 'signature' && (
            <SignatureStep
              investmentAmount={flow.investmentAmount}
              signatureImage={flow.signatureImage}
              onSignatureCapture={flow.setSignatureImage}
              onConfirm={handleContinue}
              canConfirm={flow.canContinueFrom('signature')}
            />
          )}

          {flow.step === 'processing' && (
            <ProcessingStep
              error={flow.error}
              onRetry={flow.retrySubmit}
              onChangePaymentMethod={flow.goToPaymentMethod}
            />
          )}

          {flow.step === 'complete' && flow.investmentResult && (
            <CompleteStep
              result={flow.investmentResult}
              onViewPortfolio={() => {
                onComplete(flow.investmentResult!);
              }}
              onBrowseInvestments={onBack}
            />
          )}
        </div>
      </main>
    </div>
  );
}
